"use client";

import { PencilSquareIcon } from "@heroicons/react/24/outline";
import dynamic from "next/dynamic";
import { Suspense } from "react";
import { twMerge } from "tailwind-merge";

import { Card } from "@/components/card";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { startNewChat } from "@/features/winwingpt/chat/helpers/operations/start-new-chat";
import HistoricalDialogList from "@/features/winwingpt/historical-dialog-sidebar/components/HistoricalDialogList";
import { useEventBanner } from "@/hooks/use-event-banner";
import { useLang } from "@/hooks/use-lang";

const IllustrationDialog = dynamic(
  () => import("@/features/winwingpt/dialog/IllustrationDialog"),
  {
    ssr: false,
  }
);

const Skeletons = dynamic(
  () => import("ui/widget/skeletons").then((mod) => mod.Skeletons),
  {
    ssr: false,
  }
);

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { lang } = useLang();
  const { isAuthenticated } = useAuth();
  const { showBanner } = useEventBanner();

  return (
    <section
      className={twMerge(
        "h-full @3xl:pb-4 @7xl:pb-6 sm:px-4 md:px-8 lg:px-12",
        showBanner
          ? "h-[calc(100dvh-var(--my-navbar-mask-height)-var(--my-event-banner-height)-var(--my-navbar-height))]"
          : "h-[calc(100dvh-var(--my-navbar-mask-height)-var(--my-navbar-height))]"
      )}
    >
      <div className="mx-auto grid h-full w-full max-w-[var(--my-winwingpt-width)] grid-rows-[min-content_1fr] gap-5 overflow-hidden @3xl:grid-flow-col @3xl:grid-cols-[minmax(240px,1fr)_3fr] @3xl:grid-rows-1">
        <div className="flex h-full flex-col gap-5 scrollbar-hide">
          <div className="flex items-center justify-between px-4 @3xl:px-0">
            <h1 className="text-3xl font-bold text-white @3xl:px-0">
              WinwinGPT
            </h1>
            <button
              className="rounded-md p-0.5 transition-colors hover:bg-zinc-300 hover:bg-opacity-30"
              onClick={(e) => {
                e.preventDefault();
                startNewChat({ lang });
              }}
            >
              <PencilSquareIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="hidden h-full overflow-y-scroll scrollbar-hide @3xl:block">
            <HistoricalDialogList />
          </div>
        </div>
        <Card className="relative flex h-full flex-col justify-between overflow-auto py-4 scrollbar-hide @lg:pt-8">
          <Suspense
            fallback={<Skeletons columns={1} rows={6} rowHeight={"1.5rem"} />}
          >
            {children}
          </Suspense>
        </Card>
      </div>
      {isAuthenticated && <IllustrationDialog />}
    </section>
  );
};

export default Layout;
